<template>
  <v-card class="live-stacked-bars" elevation="1">
    <v-card-title class="pb-2">
      <span class="text-h6">{{ title }}</span>
      <v-spacer />
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            v-bind="attrs"
            v-on="on"
            small
            color="grey"
          >
            mdi-information-outline
          </v-icon>
        </template>
        <span>Cliquez sur un segment pour filtrer</span>
      </v-tooltip>
    </v-card-title>

    <v-card-text class="pt-0">
      <div class="stacked-bars-container">
        <div v-if="data.length > 0">
          <highcharts
            ref="chart"
            class="chart"
            :options="chartOptions"
            :update-args="updateArgs"
          />
        </div>

        <!-- Legend toggles -->
        <div class="global-legend mt-4">
          <div class="d-flex align-center">
            <span class="text-caption font-weight-medium mr-3">Légende:</span>
            <v-chip
              small
              :color="showRunning ? 'info' : 'grey'"
              text-color="white"
              class="mr-2"
              @click="toggleLegend('running')"
            >
              <v-icon left x-small>mdi-play-circle</v-icon>
              En cours
            </v-chip>
            <v-chip
              small
              :color="showPending ? 'warning' : 'grey'"
              text-color="white"
              @click="toggleLegend('pending')"
            >
              <v-icon left x-small>mdi-clock-outline</v-icon>
              En attente
            </v-chip>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="data.length === 0" class="empty-state text-center py-6">
          <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
          <p class="text-body-2 text--secondary mt-2 mb-0">
            Aucune donnée disponible
          </p>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'LiveStackedBars',
  props: {
    title: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    maxItems: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      showRunning: true,
      showPending: true,
      isAnimating: false,
      pendingUpdate: false,
      latestPayload: null,
      animationDurationMs: 500,
      // Highcharts reactive options kept stable to avoid chart re-creation
      chartOptions: {
        accessibility: { enabled: false },
        chart: {
          type: 'bar',
          height: 200
        },
        title: { text: null },
        credits: false,
        xAxis: {
          categories: [],
          title: { text: null }
        },
        yAxis: {
          min: 0,
          title: { text: null },
          stackLabels: { enabled: false }
        },
        legend: { enabled: false },
        tooltip: {
          formatter: function() {
            const total = (this.point.y || 0);
            return `${this.x} - ${this.series.name}: <b>${total}</b>`;
          }
        },
        plotOptions: {
          series: {
            stacking: 'normal',
            cursor: 'pointer',
            animation: { duration: 500 }
          }
        },
        series: [
          { name: 'RUNNING', data: [], color: '#3B82F6' },
          { name: 'PENDING', data: [], color: '#F59E0B' }
        ]
      },
      updateArgs: [false, true, true] // redraw, oneToOne, animation
    };
  },
  computed: {
    displayedData() {
      return [...this.data]
        .sort((a, b) => b.total - a.total)
        .slice(0, this.maxItems);
    },
  },
  mounted() {
    this.updateChart();
    // Guard initial render animation: prevent updates until first animation completes
    this.$nextTick(() => {
      // Delay slightly to ensure Highcharts instance is created
      setTimeout(() => {
        const chart = this.$refs.chart && this.$refs.chart.chart;
        if (chart) {
          this.isAnimating = true;
          window.clearTimeout(this._animTimer);
          this._animTimer = window.setTimeout(() => {
            this.isAnimating = false;
            if (this.pendingUpdate && this.latestPayload) {
              const nextChart = this.$refs.chart && this.$refs.chart.chart;
              this.pendingUpdate = false;
              if (nextChart) this.applyPayload(nextChart, this.latestPayload);
              this.latestPayload = null;
            }
          }, this.animationDurationMs + 50);
        }
      }, 0);
    });
  },
  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    showRunning(val) {
      const chart = this.$refs.chart && this.$refs.chart.chart;
      if (chart && chart.series[0]) {
        chart.series[0].setVisible(val, false);
        chart.redraw(true);
      }
    },
    showPending(val) {
      const chart = this.$refs.chart && this.$refs.chart.chart;
      if (chart && chart.series[1]) {
        chart.series[1].setVisible(val, false);
        chart.redraw(true);
      }
    }
  },
  methods: {
    updateChart() {
      const chartRef = this.$refs.chart;
      const chart = chartRef && chartRef.chart;

      const categories = this.displayedData.map(it => it.name);
      const runningData = this.displayedData.map(it => it.running || 0);
      const pendingData = this.displayedData.map(it => it.pending || 0);

      const payload = { categories, runningData, pendingData };

      // Update options object minimally when chart not yet mounted
      if (!chart) {
        // Initialize options for first render
        this.chartOptions.xAxis = { ...this.chartOptions.xAxis, categories };
        this.chartOptions.series = [
          { ...this.chartOptions.series[0], data: runningData },
          { ...this.chartOptions.series[1], data: pendingData }
        ];
        this.chartOptions.chart = { ...this.chartOptions.chart, height: Math.max(200, 50 + categories.length * 40) };
        return;
      }

      // If an animation is currently running, defer update and keep only the latest payload
      if (this.isAnimating) {
        this.latestPayload = payload;
        this.pendingUpdate = true;
        return;
      }

      // Apply the payload with animation and schedule follow-up if there was a pending update
      this.applyPayload(chart, payload);
    },

    applyPayload(chart, payload) {
      const { categories, runningData, pendingData } = payload;

      if (chart.xAxis && chart.xAxis[0]) {
        chart.xAxis[0].setCategories(categories, false);
      }
      if (chart.series && chart.series[0]) {
        chart.series[0].setData(runningData, false, { duration: this.animationDurationMs }, true);
      }
      if (chart.series && chart.series[1]) {
        chart.series[1].setData(pendingData, false, { duration: this.animationDurationMs }, true);
      }

      const self = this;
      chart.update({
        chart: { height: Math.max(200, 50 + categories.length * 40) },
        plotOptions: {
          series: {
            point: {
              events: {
                click: function() {
                  const item = self.displayedData[this.x];
                  const status = this.series.name.toUpperCase();
                  if (item) {
                    self.$emit('segment-click', { queue: item.name, definition: item.name, status });
                  }
                }
              }
            }
          }
        }
      }, false, true, { duration: Math.min(300, this.animationDurationMs) });

      // Respect visibility toggles
      if (chart.series[0]) chart.series[0].setVisible(this.showRunning, false);
      if (chart.series[1]) chart.series[1].setVisible(this.showPending, false);

      // Start animation gate
      this.isAnimating = true;
      chart.redraw(true);

      // Release the gate after animation completes and process any pending update
      window.clearTimeout(this._animTimer);
      this._animTimer = window.setTimeout(() => {
        this.isAnimating = false;
        if (this.pendingUpdate) {
          this.pendingUpdate = false;
          const nextChart = this.$refs.chart && this.$refs.chart.chart;
          if (nextChart && this.latestPayload) {
            this.applyPayload(nextChart, this.latestPayload);
          }
          this.latestPayload = null;
        }
      }, this.animationDurationMs + 50);
    },
    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
      }
      return num.toString();
    },
    toggleLegend(type) {
      if (type === 'running') {
        this.showRunning = !this.showRunning;
      } else if (type === 'pending') {
        this.showPending = !this.showPending;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.live-stacked-bars {
  height: 280px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .v-card-text {
    flex: 1;
    overflow-y: auto;
  }
}

.stacked-bars-container {
  height: 100%;
}

.stacked-bar-item {
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.item-chip {
  font-size: 0.75rem;
  height: 24px;
}

.stacked-progress-container {
  position: relative;
  height: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.stacked-progress-bar {
  height: 100%;
  display: flex;
  border-radius: 4px;
  max-width: 100%;
}

.progress-segment {
  height: 100%;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;

  &:hover {
    filter: brightness(1.1);
    transform: scaleY(1.1);
  }

  &.running {
    background-color: #3B82F6; // RUNNING blue
    z-index: 2;
  }

  &.pending {
    background-color: #F59E0B; // PENDING amber
    z-index: 1;
  }

  .segment-content {
    width: 100%;
    height: 100%;
  }
}

.item-legend {
  font-size: 0.75rem;

  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &.running {
      background-color: #3B82F6; // RUNNING blue
    }

    &.pending {
      background-color: #F59E0B; // PENDING amber
    }
  }
}

.global-legend {
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;

  .v-chip {
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      transform: translateY(-1px);
    }
  }
}

.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

// Responsive adjustments
@media (max-width: 768px) {
  .live-stacked-bars {
    height: 240px;
  }

  .item-chip {
    font-size: 0.7rem;
    height: 22px;
  }

  .item-stats {
    font-size: 0.85rem;
  }

  .global-legend {
    .v-chip {
      font-size: 0.7rem;
    }
  }
}
</style>